import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { FolderArchive, Upload, File as FileIcon, Trash2, Download, Loader2, ArrowRightCircle, CheckCircle2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { useTaxYear } from "../contexts/TaxYearContext";
import { DOCUMENT_TYPES_FOR_FILE } from "../constants/documentTypes";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Stage 3 (Vault connection) — only these extensions can ever be processed
// into the Tax App. Mirrors backend VAULT_PROCESSABLE_EXTENSIONS exactly so
// the "Process in Tax App" action only ever appears where it can succeed.
const PROCESSABLE_EXTENSIONS = [".pdf", ".csv", ".xlsx", ".png", ".jpg", ".jpeg"];
function isProcessable(name) {
  const idx = (name || "").lastIndexOf(".");
  if (idx === -1) return false;
  return PROCESSABLE_EXTENSIONS.includes(name.slice(idx).toLowerCase());
}

function fmtBytes(n) {
  if (n == null) return "—";
  if (n < 1024) return n + " B";
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + " KB";
  if (n < 1024 * 1024 * 1024) return (n / 1048576).toFixed(1) + " MB";
  return (n / 1073741824).toFixed(2) + " GB";
}

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

async function readEntry(entry, prefix) {
  if (entry.isFile) {
    return await new Promise((resolve) => {
      entry.file((f) => resolve([{ file: f, path: prefix }]));
    });
  }
  if (entry.isDirectory) {
    const reader = entry.createReader();
    const sub = prefix ? prefix + "/" + entry.name : entry.name;
    const out = [];
    const next = () => new Promise((resolve) => {
      reader.readEntries(async (ents) => {
        if (ents.length === 0) { resolve(); return; }
        for (const e of ents) {
          const items = await readEntry(e, sub);
          for (const it of items) out.push(it);
        }
        await next();
        resolve();
      });
    });
    await next();
    return out;
  }
  return [];
}

export default function OnTheGoVault() {
  const navigate = useNavigate();
  const { taxYear, availableYears, getYearStatus } = useTaxYear();
  const [items, setItems] = useState(null);
  const [drag, setDrag] = useState(false);
  const [progress, setProgress] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [processingFile, setProcessingFile] = useState(null);
  const [selectedTaxYear, setSelectedTaxYear] = useState(null);
  const [selectedDocType, setSelectedDocType] = useState(DOCUMENT_TYPES_FOR_FILE[0]);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const fileRef = useRef(null);
  const folderRef = useRef(null);
  // Stage 4 — deep-link from the "From Vault" badge (?focus_file=<id>):
  // scroll the specific row into view and briefly highlight it, ONCE per id.
  const [highlightFileId, setHighlightFileId] = useState(null);
  const handledFocusFileRef = useRef(null);
  const rowRefs = useRef({});
  const [searchParams] = useSearchParams();

  const fetchList = async () => {
    try {
      const r = await axios.get(API + "/vault/list", { withCredentials: true });
      setItems(r.data.files || []);
    } catch {
      toast.error("Could not load your vault. Please refresh.");
      setItems([]);
    }
  };

  useEffect(() => { fetchList(); }, []);

  useEffect(() => {
    const focusFileId = searchParams.get("focus_file");
    if (!focusFileId || !items || items.length === 0) return;
    if (handledFocusFileRef.current === focusFileId) return;
    const el = rowRefs.current[focusFileId];
    if (!el) return;
    handledFocusFileRef.current = focusFileId;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightFileId(focusFileId);
    const t = setTimeout(() => setHighlightFileId(null), 3000);
    return () => clearTimeout(t);
  }, [items, searchParams]);

  const setFolderRef = (el) => {
    folderRef.current = el;
    if (el) {
      el.setAttribute("webkitdirectory", "");
      el.setAttribute("directory", "");
    }
  };

  const saveOne = async (entry) => {
    const fd = new FormData();
    fd.append("file", entry.file);
    fd.append("relative_path", entry.path || "");
    const r = await axios.post(API + "/vault/upload", fd, {
      withCredentials: true,
      headers: { "Content-Type": "multipart/form-data" },
    });
    return r.data;
  };

  const saveAll = async (collected) => {
    if (!collected.length) return;
    setProgress({ done: 0, total: collected.length });
    let errs = 0;
    let renames = 0;
    for (let i = 0; i < collected.length; i += 1) {
      try {
        const r = await saveOne(collected[i]);
        if (r.collision_renamed) renames += 1;
      } catch (e) {
        errs += 1;
        const detail = (e.response && e.response.data && e.response.data.detail) || "Upload failed";
        toast.error(collected[i].file.name + ": " + detail);
      }
      setProgress({ done: i + 1, total: collected.length });
    }
    setProgress(null);
    await fetchList();
    if (errs === 0) {
      if (renames === 0) toast.success("Saved " + collected.length + " file" + (collected.length === 1 ? "" : "s") + " to your vault.");
      else toast.success("Saved " + collected.length + " file" + (collected.length === 1 ? "" : "s") + " — " + renames + " renamed to avoid overwriting.");
    } else if (errs < collected.length) {
      toast.warning("Saved " + (collected.length - errs) + " of " + collected.length + " files. " + errs + " failed.");
    }
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setDrag(false);
    if (progress) return;
    const transfers = Array.from(e.dataTransfer.items || []);
    const collected = [];
    for (const t of transfers) {
      const entry = t.webkitGetAsEntry ? t.webkitGetAsEntry() : null;
      if (entry) {
        const found = await readEntry(entry, "");
        for (const f of found) collected.push(f);
      } else {
        const f = t.getAsFile();
        if (f) collected.push({ file: f, path: "" });
      }
    }
    await saveAll(collected);
  };

  const onFileInput = async (e) => {
    const list = Array.from(e.target.files || []);
    const collected = list.map((f) => {
      const rel = f.webkitRelativePath || "";
      const path = rel ? rel.split("/").slice(0, -1).join("/") : "";
      return { file: f, path: path };
    });
    e.target.value = "";
    await saveAll(collected);
  };

  const deleteOne = async (f) => {
    try {
      await axios.delete(API + "/vault/file/" + f.id, { withCredentials: true });
      toast.success(
        f.processing_state === "processed"
          ? "Vault copy deleted. Processed data remains."
          : "File deleted from Vault."
      );
      fetchList();
    } catch {
      toast.error("Could not delete file. Please try again.");
    }
  };

  const download = async (f) => {
    try {
      const r = await axios.get(API + "/vault/file/" + f.id + "/download", {
        responseType: "blob",
        withCredentials: true,
      });
      const blobUrl = window.URL.createObjectURL(new Blob([r.data], { type: f.mime_type || "application/octet-stream" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = f.original_name;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast.error("Download failed", { description: err?.response?.data?.detail || err.message });
    }
  };

  // Stage 3B — Vault processing is a WRITE action. Only years the backend
  // reports can_write:true may ever be selected here; read_only/archived/
  // inactive/unprovisioned years must never be processable.
  const writableYears = (availableYears || []).filter((y) => getYearStatus(y).can_write);

  const openProcessModal = (f) => {
    setPreview(null);
    const defaultYear = writableYears.includes(taxYear) ? taxYear : writableYears[0];
    setSelectedTaxYear(defaultYear || null);
    setSelectedDocType(DOCUMENT_TYPES_FOR_FILE[0]);
    setProcessingFile(f);
  };

  useEffect(() => {
    if (!processingFile || !selectedTaxYear || !selectedDocType) return;
    let cancelled = false;
    setPreviewLoading(true);
    setPreview(null);
    axios.get(API + "/vault/file/" + processingFile.id + "/process-preview", {
      params: { tax_year: selectedTaxYear, document_type: selectedDocType },
      withCredentials: true,
    }).then((r) => { if (!cancelled) setPreview(r.data); })
      .catch((e) => {
        if (cancelled) return;
        const detail = e.response && e.response.data && e.response.data.detail;
        const msg = (detail && detail.message) || "Could not load a preview for this file.";
        setPreview({ error: msg });
      })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [processingFile, selectedTaxYear, selectedDocType]);

  const confirmProcess = async () => {
    if (!processingFile) return;
    setConfirming(true);
    try {
      await axios.post(API + "/vault/file/" + processingFile.id + "/process", {
        tax_year: selectedTaxYear, document_type: selectedDocType,
      }, { withCredentials: true });
      toast.success("Processed into Tax Year " + selectedTaxYear + ". Review it in Upload & Extract.");
      setProcessingFile(null);
      await fetchList();
    } catch (e) {
      const detail = e.response && e.response.data && e.response.data.detail;
      const msg = (detail && detail.message) || "Could not process this file. Please try again.";
      toast.error(msg);
    } finally {
      setConfirming(false);
    }
  };

  const totalBytes = (items || []).reduce((s, f) => s + (f.size_bytes || 0), 0);
  const busy = !!progress;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="vault-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 flex items-center gap-3" style={{ fontFamily: "Manrope" }}>
            <FolderArchive className="w-7 h-7 text-blue-600" />
            On-The-Go Vault
          </h1>
          <p className="text-slate-500 text-sm mt-2">
            Drop a folder or files here to keep them with your account.
          </p>
        </div>
        {items && items.length > 0 && (
          <div className="text-right">
            <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Vault Size</p>
            <p className="text-lg font-bold text-slate-900" data-testid="vault-total-size">{fmtBytes(totalBytes)}</p>
            <p className="text-xs text-slate-500">{items.length} file{items.length === 1 ? "" : "s"}</p>
          </div>
        )}
      </div>

      <div
        onDragEnter={(e) => { e.preventDefault(); setDrag(true); }}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDrag(false); }}
        onDrop={onDrop}
        onClick={() => !busy && fileRef.current && fileRef.current.click()}
        className={"border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors " + (drag ? "border-blue-500 bg-blue-50" : "border-slate-300 bg-white hover:border-blue-400 hover:bg-slate-50") + (busy ? " opacity-60 pointer-events-none" : "")}
        data-testid="vault-dropzone"
      >
        <input ref={fileRef} type="file" multiple onChange={onFileInput} className="hidden" data-testid="vault-file-input" />
        <input ref={setFolderRef} type="file" multiple onChange={onFileInput} className="hidden" data-testid="vault-folder-input" />
        <Upload className={"w-10 h-10 mx-auto mb-3 " + (drag ? "text-blue-600" : "text-slate-400")} />
        {busy ? (
          <div className="space-y-2">
            <p className="text-slate-700 font-semibold flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Saving {progress.done} of {progress.total} files…
            </p>
            <div className="max-w-md mx-auto bg-slate-200 rounded-full h-2 overflow-hidden">
              <div className="bg-blue-500 h-full transition-all" style={{ width: Math.round((progress.done / progress.total) * 100) + "%" }} />
            </div>
          </div>
        ) : (
          <div>
            <p className="text-slate-700 font-semibold">Drop a folder or files here</p>
            <p className="text-slate-500 text-sm mt-1">
              or
              <button type="button" onClick={(e) => { e.stopPropagation(); fileRef.current && fileRef.current.click(); }} className="text-blue-600 hover:underline font-medium ml-1" data-testid="vault-choose-files-btn">choose files</button>
              <span> · </span>
              <button type="button" onClick={(e) => { e.stopPropagation(); folderRef.current && folderRef.current.click(); }} className="text-blue-600 hover:underline font-medium" data-testid="vault-choose-folder-btn">choose a folder</button>
            </p>
            <p className="text-slate-400 text-xs mt-4" data-testid="vault-accepted-types">
              Accepted: PDF, images (JPG, PNG, HEIC), spreadsheets (XLS, XLSX, CSV), Word documents, and plain text files.
              Executable files (.exe, .bat, .sh, .msi, etc.) are not allowed. Max 100 MB per file.
            </p>
          </div>
        )}
      </div>

      {items === null ? (
        <div className="h-32 bg-slate-100 rounded-xl animate-pulse" data-testid="vault-loading" />
      ) : items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center" data-testid="vault-empty">
          <FolderArchive className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500 font-medium">Your vault is empty.</p>
          <p className="text-slate-400 text-xs mt-1">Drop a folder above to get started.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="vault-list">
          <div className="px-4 py-3 border-b border-slate-100">
            <span className="text-sm font-semibold text-slate-700">{items.length} item{items.length === 1 ? "" : "s"}</span>
          </div>
          <div className="divide-y divide-slate-50">
            {items.map((f) => (
              <div
                key={f.id}
                ref={(el) => { rowRefs.current[f.id] = el; }}
                className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${highlightFileId === f.id ? "bg-emerald-50 ring-2 ring-inset ring-emerald-300" : ""}`}
                data-testid={"vault-item-" + f.id}
              >
                <FileIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <span className="text-slate-600 text-xs flex-shrink-0">{f.relative_path || "(root)"}</span>
                <span className="text-slate-400 flex-shrink-0">/</span>
                <span className="text-slate-800 truncate flex-1" title={f.original_name}>{f.original_name}</span>
                <span className="text-xs text-slate-400 flex-shrink-0">{fmtBytes(f.size_bytes)}</span>
                <span className="text-xs text-slate-400 hidden md:inline flex-shrink-0">· {fmtDate(f.uploaded_at)}</span>
                {f.processing_state === "processed" ? (
                  <button
                    type="button"
                    onClick={() => navigate(f.processed_document_id ? `/app/upload?focus_doc=${f.processed_document_id}` : "/app/upload")}
                    title="View this document in Upload & Extract"
                    className="text-xs text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-2 py-1 rounded-full flex-shrink-0 font-medium transition-colors flex items-center gap-1"
                    data-testid={"vault-item-processed-" + f.id}
                  >
                    <CheckCircle2 className="w-3 h-3" /> Processed — Tax Year {f.processed_tax_year}
                  </button>
                ) : isProcessable(f.original_name) ? (
                  <button
                    type="button"
                    onClick={() => openProcessModal(f)}
                    className="text-xs text-blue-700 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded-full flex-shrink-0 font-medium transition-colors flex items-center gap-1"
                    data-testid={"vault-item-process-" + f.id}
                  >
                    <ArrowRightCircle className="w-3 h-3" /> Process in Tax App
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => download(f)}
                  title="Download"
                  className="text-slate-400 hover:text-blue-600 transition-colors p-1 rounded flex-shrink-0"
                  data-testid={"vault-item-download-" + f.id}
                >
                  <Download className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setPendingDelete(f)}
                  title="Delete"
                  className="text-slate-400 hover:text-red-600 transition-colors p-1 rounded flex-shrink-0"
                  data-testid={"vault-item-delete-" + f.id}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => { if (!o) setPendingDelete(null); }}>
        <AlertDialogContent data-testid="vault-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this file?</AlertDialogTitle>
            <AlertDialogDescription data-testid="vault-delete-description">
              "{pendingDelete && pendingDelete.original_name}" will be permanently removed from your vault. This cannot be undone.
              {pendingDelete && pendingDelete.processing_state === "processed" && (
                <span className="block mt-2 text-amber-700 font-medium" data-testid="vault-delete-processed-warning">
                  This file was processed into Tax Year {pendingDelete.processed_tax_year}. Deleting the Vault copy will not remove its processed document or transactions.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="vault-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="vault-delete-confirm-btn"
              className="bg-red-600 hover:bg-red-700"
              onClick={async () => {
                const f = pendingDelete;
                setPendingDelete(null);
                if (f) await deleteOne(f);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!processingFile} onOpenChange={(o) => { if (!o && !confirming) setProcessingFile(null); }}>
        <DialogContent data-testid="vault-process-modal">
          <DialogHeader>
            <DialogTitle>Process in Tax App</DialogTitle>
            <DialogDescription>
              "{processingFile && processingFile.original_name}". After processing, go to Upload &amp; Extract, review the transactions, select a Category, and Approve.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Tax Year</label>
              {writableYears.length > 0 ? (
                <select
                  value={selectedTaxYear || ""}
                  onChange={(e) => setSelectedTaxYear(parseInt(e.target.value, 10))}
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  data-testid="vault-process-tax-year-select"
                >
                  {writableYears.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              ) : (
                <button
                  type="button"
                  data-testid="vault-process-activate-prompt"
                  onClick={() => navigate("/app/year-end/activate?tax_year=" + (taxYear || ""))}
                  className="mt-1 w-full flex items-center justify-center gap-1 text-xs font-bold uppercase tracking-wider text-amber-700 bg-amber-100 hover:bg-amber-200 border border-amber-200 rounded-lg px-3 py-2 transition-colors"
                >
                  No active tax year — Activate a year to process this file
                </button>
              )}
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Document Type</label>
              <select
                value={selectedDocType}
                onChange={(e) => setSelectedDocType(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                data-testid="vault-process-doc-type-select"
              >
                {DOCUMENT_TYPES_FOR_FILE.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm" data-testid="vault-process-preview">
              {previewLoading ? (
                <p className="text-slate-500 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking pages…</p>
              ) : preview && preview.error ? (
                <p className="text-red-600" data-testid="vault-process-preview-error">{preview.error}</p>
              ) : preview ? (
                <div className="space-y-1 text-slate-700">
                  <p>{preview.is_spreadsheet ? "Spreadsheet — no pages will be charged." : "Pages needed: " + preview.pages_needed}</p>
                  <p className={preview.pages_remaining < preview.pages_needed ? "text-red-600 font-medium" : "text-slate-500"}>
                    Pages remaining for Tax Year {selectedTaxYear}: {preview.pages_remaining === 999999 ? "Unlimited" : preview.pages_remaining}
                  </p>
                </div>
              ) : null}
            </div>
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={() => setProcessingFile(null)}
              disabled={confirming}
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 disabled:opacity-50"
              data-testid="vault-process-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={
                confirming ||
                previewLoading ||
                !selectedTaxYear ||
                !preview ||
                !!preview.error ||
                (typeof preview.pages_remaining === "number" &&
                  typeof preview.pages_needed === "number" &&
                  preview.pages_remaining < preview.pages_needed)
              }
              onClick={confirmProcess}
              className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="vault-process-confirm-btn"
            >
              {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Confirm &amp; Process
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
